// Client-side China access gate.
//
// Layered, best-effort block:
//   1. Synchronous heuristics (bot user-agent, timezone, language) that run
//      before React mounts.
//   2. Asynchronous IP geolocation against multiple independent endpoints,
//      cached for the session, that blocks CN / HK / MO addresses.
//
// This is NOT a substitute for true IP-level blocking at the CDN/WAF layer.
// For guaranteed blocking of all China/HK/MO IPs and bot scrapers, a
// Cloudflare WAF rule `(ip.geoip.country in {"CN" "HK" "MO"})` must be added
// in front of the site.

const BLOCKED_COUNTRIES = new Set<string>(["CN", "HK", "MO"]);

const CACHE_KEY = "psa_geo_gate";

// Only IP-confirmed blocks persist across sessions (localStorage), and even
// those expire after BLOCKED_TTL_MS so a stale false positive (e.g. a laptop
// briefly on a VPN, or a wrong timezone) cannot lock a visitor out forever.
// Heuristic-only blocks and "allowed" verdicts stay session-scoped.
const BLOCKED_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function readVerdict(): string | null {
  try {
    const persisted = localStorage.getItem(CACHE_KEY);
    if (persisted) {
      const [value, ts] = persisted.split("|");
      if (value === "blocked" && Date.now() - Number(ts) < BLOCKED_TTL_MS) {
        return "blocked";
      }
      localStorage.removeItem(CACHE_KEY);
    }
    return sessionStorage.getItem(CACHE_KEY);
  } catch {
    return null;
  }
}

function writeVerdict(
  value: "blocked" | "allowed",
  opts?: { confirmed?: boolean },
): void {
  try {
    if (value === "blocked" && opts?.confirmed) {
      localStorage.setItem(CACHE_KEY, `blocked|${Date.now()}`);
    }
    sessionStorage.setItem(CACHE_KEY, value);
  } catch {
    /* ignore */
  }
}

const BLOCKED_TIMEZONES = new Set<string>([
  "Asia/Shanghai",
  "Asia/Chongqing",
  "Asia/Chungking",
  "Asia/Harbin",
  "Asia/Urumqi",
  "Asia/Kashgar",
  "Asia/Hong_Kong",
  "Asia/Macau",
  "Asia/Macao",
  "PRC",
  "Hongkong",
]);

// Known Chinese scraper / bot user-agents plus generic scraping clients.
// Blocked regardless of IP.
const BLOCKED_UA_RE =
  /bytespider|bytedance|tiktokspider|petalbot|aspiegelbot|sogou|baidu|yisou|easou|jike|yodao|youdao|netease|360spider|haosou|qihoo|shenma|toutiao|tencent|qqbrowser|weibo|alibaba|aliyun|taobao|coccocbot|yandex|mj12bot|ahrefsbot|semrushbot|dotbot|dataforseobot|blexbot|seznambot|zgrab|masscan|nmap|curl\/|wget\/|libwww-perl|python-requests|python-urllib|aiohttp|httpx|scrapy|go-http-client|okhttp|java\/|apache-httpclient|node-fetch|axios\/|headlesschrome|phantomjs|puppeteer|playwright|selenium|http_request2|winhttp|lwp::simple/i;

function heuristicBlocked(): boolean {
  try {
    const ua = navigator.userAgent || "";
    if (!ua) return true;
    if (BLOCKED_UA_RE.test(ua)) return true;
    if ((navigator as unknown as { webdriver?: boolean }).webdriver === true) return true;

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (BLOCKED_TIMEZONES.has(tz)) return true;

    const langs = [
      navigator.language || "",
      ...((navigator.languages as readonly string[] | undefined) ?? []),
    ].map((l) => l.toLowerCase());

    // Only block locales carrying an explicit mainland/HK/MO *region* subtag
    // (zh-CN, zh-Hans-CN, zh-CN-u-ca-chinese, zh-HK, zh-MO ...).
    // Generic "zh" and script-only tags like "zh-Hans" are location-
    // independent (used in Taiwan, Singapore, and worldwide) and are
    // intentionally NOT blocked.
    const REGION_RE = /^zh(?:-[a-z]{4})?-(cn|hk|mo)(?:-|$)/;
    if (langs.some((l) => REGION_RE.test(l))) {
      return true;
    }

  } catch {
    return false;
  }
  return false;
}

function renderBlockPage(): void {
  document.documentElement.innerHTML = `
 <head>
 <meta charset="utf-8" />
 <meta name="viewport" content="width=device-width, initial-scale=1" />
 <meta name="robots" content="noindex, nofollow" />
 <title>Access restricted</title>
 <style>
 html,body{margin:0;padding:0;height:100%;background:#0f1729;color:#e6edf3;
 font-family:-apple-system,BlueGrotesk,"Segoe UI",Roboto,sans-serif;}
 .wrap{min-height:100%;display:flex;align-items:center;justify-content:center;padding:2rem;}
 .card{max-width:520px;text-align:center;}
 h1{font-size:1.5rem;margin:0 0 .75rem;color:#1DA88A;}
 p{line-height:1.6;color:#b8c1cf;margin:.5rem 0;}
 </style>
 </head>
 <body>
 <div class="wrap">
 <div class="card">
 <h1>Access restricted</h1>
 <p>Point Science Analytics is not available in your region.</p>
 <p>If you believe this is an error, please contact support.</p>
 </div>
 </div>
 </body>
 `;
}

/**
 * Synchronous check. Returns true if the visitor was blocked and the page
 * was replaced with the restricted-access notice. Call before mounting React.
 */
export function enforceChinaBlock(): boolean {
  if (typeof window === "undefined") return false;
  const cached = readVerdict();
  if (cached === "blocked") {
    renderBlockPage();
    return true;
  }
  if (!heuristicBlocked()) return false;
  // Heuristic-only block: session-scoped, not persisted, so a false
  // positive (wrong timezone, shared machine) clears on browser restart.
  writeVerdict("blocked");
  renderBlockPage();
  return true;
}

/**
 * Authoritative server-side check. The backend resolves the visitor's real IP
 * to a country (spoof-proof, unlike browser timezone/locale) and tells us
 * whether to serve the page. Fails open on any network/backend error.
 */
export async function enforceServerGeoBlock(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
    if (!base || !key) return false;

    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    const resp = await fetch(`${base}/functions/v1/geo-check`, {
      method: "POST",
      signal: ctrl.signal,
      cache: "no-store",
      headers: { "Content-Type": "application/json", apikey: key, Authorization: `Bearer ${key}` },
      body: "{}",
    });
    clearTimeout(t);
    if (!resp.ok) return false;
    const data = (await resp.json()) as {
      blocked?: boolean;
      confirmed?: boolean;
      reason?: string;
    };
    if (data?.blocked === true) {
      // Only a multi-provider (or Cloudflare) region verdict is persisted.
      // Heuristic verdicts (e.g. user-agent) stay session-scoped so a single
      // misclassification never locks a visitor out across sessions.
      const confirmed = data.confirmed === true && data.reason === "region";
      writeVerdict("blocked", { confirmed });
      renderBlockPage();
      return true;
    }
  } catch {
    /* fail open */
  }
  return false;
}

async function lookupCountry(
  url: string,
  parse: (body: string) => string,
  signal: AbortSignal,
): Promise<string> {
  const resp = await fetch(url, { signal, cache: "no-store", credentials: "omit" });
  if (!resp.ok) return "";
  return parse(await resp.text()).toUpperCase();
}

/**
 * Asynchronous IP-based geo check. Queries several independent geolocation
 * endpoints and blocks the page if any of them resolves the visitor to
 * China / Hong Kong / Macau. Fails open on network errors so legitimate users
 * are never locked out by an outage.
 */
export async function enforceChinaBlockByIp(): Promise<void> {
  if (typeof window === "undefined") return;
  const verdict = readVerdict();
  if (verdict === "allowed") return;
  // A persisted (IP-confirmed, unexpired) block is authoritative — don't
  // re-probe. A session-only heuristic block IS re-checked below, so a false
  // positive can be cleared by two clean geo results.
  if (verdict === "blocked") {
    try {
      if (localStorage.getItem(CACHE_KEY)?.startsWith("blocked|")) {
        renderBlockPage();
        return;
      }
    } catch {
      renderBlockPage();
      return;
    }
  }

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 4000);

  const probes: Promise<string>[] = [
    lookupCountry(
      "https://www.cloudflare.com/cdn-cgi/trace",
      (text) => /(?:^|\n)loc=([A-Za-z]{2})/.exec(text)?.[1] ?? "",
      ctrl.signal,
    ),
    lookupCountry(
      "https://ipapi.co/json/",
      (text) => {
        try {
          return String(JSON.parse(text)?.country_code ?? "");
        } catch {
          return "";
        }
      },
      ctrl.signal,
    ),
    lookupCountry(
      "https://get.geojs.io/v1/ip/country.json",
      (text) => {
        try {
          return String(JSON.parse(text)?.country ?? "");
        } catch {
          return "";
        }
      },
      ctrl.signal,
    ),
    lookupCountry(
      "https://ipwho.is/",
      (text) => {
        try {
          return String(JSON.parse(text)?.country_code ?? "");
        } catch {
          return "";
        }
      },
      ctrl.signal,
    ),
    lookupCountry(
      "https://api.country.is/",
      (text) => {
        try {
          return String(JSON.parse(text)?.country ?? "");
        } catch {
          return "";
        }
      },
      ctrl.signal,
    ),
  ];

  const results = await Promise.allSettled(probes);
  clearTimeout(t);

  const countries = results
    .map((r) => (r.status === "fulfilled" ? r.value : ""))
    .filter(Boolean);

  // Require two independent blocked-region confirmations. A lone CN/HK/MO
  // result must not lock out a visitor when other probes fail or disagree.
  const blockedVotes = countries.filter((c) => BLOCKED_COUNTRIES.has(c)).length;
  if (blockedVotes >= 2) {
    // Two independent geo providers agree: persist, but with a 24h TTL so a
    // transient VPN or stale lookup can never lock someone out permanently.
    writeVerdict("blocked", { confirmed: true });
    renderBlockPage();
    return;
  }


  // Do not turn a single response into a session-long allow decision. An
  // inconclusive lookup is retried on the next page load, which matters in
  // regions where some providers are unreachable.
  const allowedVotes = countries.length - blockedVotes;
  if (allowedVotes >= 2) {
    // If a heuristic-only session block was showing, clear it and reload so
    // the real app mounts — false positives recover automatically.
    const wasSessionBlocked = (() => {
      try {
        return sessionStorage.getItem(CACHE_KEY) === "blocked";
      } catch {
        return false;
      }
    })();
    writeVerdict("allowed");
    if (wasSessionBlocked) window.location.reload();
  }
}
